import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import EditorHelpers from "~/helpers/editor_helpers";

const VariableMenuButton = ({
  inputRef,
  value = "",
  onChange,
  includeOwner = true,
  includeAlertVariables = false,
  disabled = false,
  title = "Variables",
}) => {
  const [open, setOpen] = useState(false);
  const [activeGroups, setActiveGroups] = useState([]);
  const containerRef = useRef(null);
  const groups = useMemo(
    () =>
      EditorHelpers.variableGroups({
        includeSender: false,
        includeOwner,
        includeAlertVariables,
      }),
    [includeOwner, includeAlertVariables],
  );

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!open) {
      setActiveGroups([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const insertToken = (token) => {
    const input = inputRef?.current;
    const currentValue = value ?? "";
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    const nextValue = `${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`;

    onChange(nextValue);
    setOpen(false);

    window.requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const renderVariables = (variables = []) =>
    variables.map((variable) => (
      <button
        key={variable.value}
        type="button"
        className="dropdown-item"
        title={variable.tooltip}
        role="menuitem"
        onClick={() => insertToken(variable.value)}
      >
        {variable.text}
      </button>
    ));

  const currentGroup = activeGroups.reduce(
    (group, groupName) => group?.groups?.find(({ text }) => text === groupName),
    { groups },
  );
  const visibleGroups = currentGroup?.groups || [];
  const menuLabel = activeGroups.length
    ? `${title}: ${activeGroups.join(" > ")}`
    : title;

  const renderGroup = (group) => (
    <button
      key={group.text}
      type="button"
      className="dropdown-item d-flex justify-content-between align-items-center"
      role="menuitem"
      onClick={() => setActiveGroups((current) => [...current, group.text])}
    >
      <span>{group.text}</span>
      <span aria-hidden="true">&gt;</span>
    </button>
  );

  return (
    <div className="dropdown" ref={containerRef}>
      <button
        type="button"
        className="btn btn-outline-secondary"
        aria-label={title}
        aria-expanded={open && !disabled}
        aria-haspopup="true"
        title={title}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <FontAwesomeIcon icon={["fas", "fa-users"]} />
      </button>

      {open && !disabled && (
        <div
          className="dropdown-menu show ss-variable-menu-button__menu"
          role="menu"
          aria-label={menuLabel}
        >
          {activeGroups.length > 0 && (
            <button
              type="button"
              className="dropdown-item"
              role="menuitem"
              onClick={() => setActiveGroups((current) => current.slice(0, -1))}
            >
              Back
            </button>
          )}
          {renderVariables(currentGroup?.variables)}
          {visibleGroups.map((group) => renderGroup(group))}
        </div>
      )}
    </div>
  );
};

VariableMenuButton.propTypes = {
  inputRef: PropTypes.shape({ current: PropTypes.shape({}) }),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  includeOwner: PropTypes.bool,
  includeAlertVariables: PropTypes.bool,
  disabled: PropTypes.bool,
  title: PropTypes.string,
};

export default VariableMenuButton;
